/** Microsoft Clarity: mapa de calor e gravação de sessão do portal.
 *
 * Projeto separado do site institucional (`site/index.html`), porque aqui é
 * área logada: quem visita a página de vendas não tem nada a ver com quem já
 * é da loja, e os dois precisam de mascaramento diferente.
 *
 * O projeto do portal está no modo **rígido** — todo texto sai borrado na
 * gravação. Aqui dentro quase tudo é nome, CPF, e-mail e saldo de irmão, e o
 * que o Clarity tem de mostrar é onde se clica e onde se trava, não o que
 * está escrito. Só a moldura das telas (`MOLDURA`, abaixo) sai legível.
 *
 * Sem `VITE_CLARITY_ID` não carrega nada, e o `.env.development` é vazio de
 * propósito: sessão de quem está programando não vira dado de uso.
 */

/** A moldura das telas — o texto que é igual em toda Loja — sai legível na gravação.
 *
 * O projeto continua no modo rígido; daqui se libera, por seletor, só o que não é
 * dado: menu, nome da Loja, títulos, cabeçalhos de tabela, abas, rótulos de campo
 * e de indicador. Nome, CPF, valor e e-mail ficam nas células, nos valores e nos
 * campos, que nada aqui toca — e campo de digitação e lista de seleção o Clarity
 * mascara em qualquer modo. A política de privacidade (seção 14) promete isso:
 * seletor novo aqui tem de ser moldura, nunca conteúdo.
 */
const MOLDURA = [
  '.sid-brand',
  '.mh-title',
  '.nav',
  '#mobile-nav',
  '#mobile-mais',
  '.pg-h',
  '.card-t > span:first-child',
  'thead',
  '.abas',
  '.seg',
  '.kpi-l',
  '.pl',
  '.s3-l',
  '.campo > label',
  'label.campo > span:first-child',
  // Entrada: a vitrine é texto fixo; o título é o nome da loja escolhida.
  '.acesso-vitrine',
  '.login-title',
  '.login-separator',
  '.login-btn',
  '.login-links',
].join(',');

/** Botão também é moldura, menos quando o rótulo pode ser dado: com número no
 *  texto (valor, data) ou com `title`/`aria-label`, onde costuma ir o nome
 *  ("Remover Fulano"). Liberar o botão libera os atributos junto. */
const BOTOES = '.btn';

function liberar(raiz: Element): void {
  const marcar = (el: Element) => {
    // `data-clarity-mask` explícito vence: liberar um filho dele o descobriria.
    if (!el.hasAttribute('data-clarity-unmask') && !el.closest('[data-clarity-mask]')) {
      el.setAttribute('data-clarity-unmask', 'true');
    }
  };
  if (raiz.matches(MOLDURA)) marcar(raiz);
  raiz.querySelectorAll(MOLDURA).forEach(marcar);

  const botoes = [...raiz.querySelectorAll(BOTOES)];
  if (raiz.matches(BOTOES)) botoes.push(raiz);
  for (const botao of botoes) {
    if (botao.hasAttribute('title') || botao.hasAttribute('aria-label')) continue;
    if (/\d/.test(botao.textContent ?? '')) continue;
    marcar(botao);
  }
}

/** Marca a moldura em tudo o que o React põe na tela.
 *
 * Tem de ser criado antes do script do Clarity: observadores de mutação são
 * avisados na ordem em que foram criados, então a marca chega ao elemento antes
 * de o Clarity ler o nó novo — e o que já estava na tela ele lê marcado ao carregar. */
function observarMoldura(): void {
  liberar(document.documentElement);
  new MutationObserver((mudancas) => {
    for (const mudanca of mudancas) {
      mudanca.addedNodes.forEach((no) => {
        if (no instanceof Element) liberar(no);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}

declare global {
  interface Window {
    clarity?: ((...argumentos: unknown[]) => void) & { q?: unknown[][] };
  }
}

/** Põe o Clarity de pé, se houver projeto configurado. */
export function iniciarClarity(): void {
  const id = import.meta.env.VITE_CLARITY_ID as string | undefined;
  if (!id || window.clarity) return;

  // Até o script da Microsoft chegar, `window.clarity` é só uma fila: o que
  // for chamado antes fica em `.q` e é processado na carga.
  const fila = ((...argumentos: unknown[]) => {
    (fila.q = fila.q ?? []).push(argumentos);
  }) as NonNullable<Window['clarity']>;
  window.clarity = fila;

  observarMoldura();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${id}`;
  document.head.appendChild(script);
}
